import { Placement } from '@popperjs/core'
import { FC, ReactElement, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { usePopper } from 'react-popper'

import { classNames } from '../../functions'
import { useDebounce } from '../../hooks/utils/useDebounce'

interface TooltipProps {
  panel: ReactElement
  button: ReactElement
  placement?: Placement
  className?: string
}

export const Tooltip: FC<TooltipProps> = ({
  panel,
  button,
  placement = 'bottom',
  className = '',
}) => {
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const [debouncedHover, setDebouncedHover] = useState(false)
  const [hover, setHover] = useState(false)

  const DebouncedHover = useDebounce([debouncedHover, hover], 200)
  useEffect(() => {
    const [debouncedHover] = DebouncedHover
    setHover(debouncedHover)
  }, [DebouncedHover])

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 12],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 500,
        },
      },
    ],
  })

  const onHover = (show: boolean) => {
    if (show) {
      setHover(true)
      setDebouncedHover(true)
      update && update()
    } else {
      setTimeout(() => {
        setDebouncedHover(false)
        update && update()
      })
    }
  }

  return (
    <div
      id="refelement"
      aria-describedby="tooltip"
      ref={setReferenceElement}
      onMouseLeave={() => onHover(false)}
      onMouseEnter={() => onHover(true)}
    >
      {button}
      <>
        {ReactDOM.createPortal(
          <div
            role="tooltip"
            id="tooltip"
            className={classNames(
              className,
              'z-[1280] select-none block leading-normal  bg-slate-700 text-slate-200 text-xs px-3 py-2 rounded-lg ',
              Boolean(!hover) ? 'invisible opacity-0 pointer-events-none' : 'opacity-1'
            )}
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            <div
              id="arrow"
              className="!transition-none"
              ref={setArrowElement}
              data-popper-arrow
            ></div>

            {panel}
          </div>,
          document.querySelector('#__next') as Element
        )}
      </>
    </div>
  )
}
